import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { CheckCircle, XCircle, Loader2, CreditCard } from 'lucide-react';
import toast from 'react-hot-toast';

const Payment = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [paymentStatus, setPaymentStatus] = useState('pending'); // pending, success, error
  const [loading, setLoading] = useState(false);
  const [creatingCheckout, setCreatingCheckout] = useState(false);
  
  const reason = searchParams.get('reason'); // 'video_limit' or 'idea_limit'
  const returnPath = searchParams.get('return') || '/trend-spotter';

  const handlePaymentComplete = async () => {
    setLoading(true);
    try {
      // Poll for payment verification
      const maxAttempts = 30; // 30 seconds max
      let attempts = 0;
      
      const checkPayment = async () => {
        const response = await fetch('/api/payment/verify', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include'
        });
        
        const result = await response.json();
        if (result.success && result.hasPaid) {
          setPaymentStatus('success');
          toast.success('Payment verified! You can now continue.');
          setTimeout(() => {
            navigate(returnPath);
          }, 2000);
          return true;
        }
        return false;
      };

      // Check immediately
      if (await checkPayment()) {
        return;
      }

      // Poll every second
      const interval = setInterval(async () => {
        attempts++;
        if (await checkPayment() || attempts >= maxAttempts) {
          clearInterval(interval);
          if (attempts >= maxAttempts) {
            setPaymentStatus('error');
            toast.error('Payment verification timed out. Please contact support if payment was successful.');
          }
        }
      }, 1000);

      // Cleanup after max attempts
      setTimeout(() => {
        clearInterval(interval);
        if (paymentStatus === 'pending') {
          setPaymentStatus('error');
        }
      }, maxAttempts * 1000);
    } catch (error) {
      console.error('Error verifying payment:', error);
      setPaymentStatus('error');
      toast.error('Error verifying payment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getLimitMessage = () => {
    if (reason === 'video_limit') {
      return {
        title: 'Video Library Limit Reached',
        message: 'You have reached the free tier limit of 30 videos. Upgrade to continue adding videos to your library.',
        limit: '30 videos'
      };
    } else if (reason === 'idea_limit') {
      return {
        title: 'Daily Brief Limit Reached',
        message: "You've used all 5 of today's briefs! 🎉 Unlock unlimited briefs for $30/month — cancel anytime, no questions asked. Or wait until tomorrow for your limit to reset.",
        limit: '5 briefs per day'
      };
    }
    return {
      title: 'Upgrade Required',
      message: 'You have reached a free tier limit. Upgrade to continue using all features.',
      limit: 'Free tier limit'
    };
  };

  const limitInfo = getLimitMessage();

  const handleSubscribe = async () => {
    if (!user) {
      toast.error('Please log in to subscribe');
      return;
    }

    setCreatingCheckout(true);
    try {
      const response = await fetch('/api/payment/create-checkout', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include' // Important: sends session cookie for authentication
      });

      const result = await response.json();
      if (result.success && result.url) {
        // Redirect to Stripe Checkout
        // The checkout session includes:
        // - customer_email: user.email (pre-filled)
        // - metadata.user_id: user.id (for webhook linking)
        // - client_reference_id: user.id (backup linking)
        window.location.href = result.url;
      } else {
        toast.error(result.error || 'Failed to create checkout session');
        setCreatingCheckout(false);
      }
    } catch (error) {
      console.error('Error creating checkout:', error);
      toast.error('Failed to create checkout session');
      setCreatingCheckout(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-zinc-900 via-zinc-800 to-zinc-900 flex items-center justify-center p-4">
      <div className="max-w-2xl w-full">
        <div className="bg-zinc-800/50 backdrop-blur-sm rounded-2xl shadow-2xl border border-zinc-700/50 p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-3">
              {limitInfo.title}
            </h1>
            <p className="text-zinc-400 text-base sm:text-lg">
              {limitInfo.message}
            </p>
          </div>

          {/* Payment Status */}
          {paymentStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-500/20 border border-green-500/50 rounded-lg flex items-center gap-3">
              <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
              <div>
                <p className="text-green-400 font-semibold">Payment Verified!</p>
                <p className="text-green-300 text-sm">Redirecting you back...</p>
              </div>
            </div>
          )}

          {paymentStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-500/20 border border-red-500/50 rounded-lg flex items-center gap-3">
              <XCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
              <div>
                <p className="text-red-400 font-semibold">Payment Verification Failed</p>
                <p className="text-red-300 text-sm">Please contact support if payment was successful.</p>
              </div>
            </div>
          )}

          {/* Programmatic Checkout Button */}
          <div className="mb-6">
            <button
              onClick={handleSubscribe}
              disabled={creatingCheckout || !user}
              className="w-full px-6 py-4 bg-blue-600 hover:bg-blue-500 disabled:bg-zinc-700 disabled:text-zinc-500 text-white font-semibold text-lg rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {creatingCheckout ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Loading...</span>
                </>
              ) : (
                <>
              <CreditCard className="h-5 w-5" />
                  <span>Subscribe Now - $30/month</span>
                </>
              )}
            </button>
            <p className="text-zinc-400 text-xs text-center mt-3">
              Your payment will be processed automatically. The subscription status will update shortly after payment completion.
            </p>
            <p className="text-zinc-500 text-xs text-center mt-2">
              Your ShowStop account email ({user?.email}) will be pre-filled in the checkout form.
            </p>
          </div>

          {/* Manual Verification Button */}
          <div className="mt-6 pt-6 border-t border-zinc-700">
            <button
              onClick={handlePaymentComplete}
              disabled={loading || paymentStatus === 'success'}
              className="w-full px-6 py-3 bg-zinc-700 hover:bg-zinc-600 disabled:bg-zinc-800 disabled:text-zinc-500 text-white font-semibold rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span>Verifying Payment...</span>
                </>
              ) : (
                <span>I've Completed Payment - Verify Now</span>
              )}
            </button>
            <p className="text-zinc-500 text-xs text-center mt-2">
              Click this button after completing payment to verify your payment
            </p>
          </div>

          {/* Back Button */}
          <div className="mt-6 text-center">
            <button
              onClick={() => navigate(returnPath)}
              className="text-zinc-400 hover:text-zinc-300 text-sm underline"
            >
              Go Back
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Payment;

